import { MinusCircleOutlined, PlusCircleOutlined } from '@ant-design/icons'
import { Button, Col, message, Modal, Row, Space, Spin, Table, Tabs, Tooltip } from 'antd'
import dayjs from 'dayjs'
import { Moment } from 'moment'
import { creditCardSVG } from 'assets/svg/creditCard'
import { useSelector } from 'hooks/useSelector'
import { log } from 'logger'
import { useEffect, useRef, useState } from 'react'
import { useMediaQuery } from 'react-responsive'
import { theme } from 'theme'
import { editGreySVG } from '../../assets/svg/edit'
import { magnifyBlackSVG } from '../../assets/svg/magnify'
import { plusSVG } from '../../assets/svg/plus'
import { ButtonIcon, ButtonIconCircle, ButtonModal } from '../../atom/button'
import { ResponsiveTable } from '../../atom/responsive-table'
import { TableWrapper } from '../../atom/table-wrapper'
import { ButtonTextSpacing } from '../../atom/user-invite'
import { ReusableSelector } from 'components/reusableSelector/selector'
import { DateTimeV2 } from 'components/date-time'
import {
  CustomBox,
  DriverSvgSpacing,
  EditButtonWrapper,
  InviteButtonCircleWrapper,
  InviteButtonWrapper,
  LineBreak,
  MultiTenantMobileStyling,
  MultiTenantStyling,
  SearchButtonContainer,
  SearchButtonWrapper,
  TableStyling,
  TabsButtonWrapper,
  TabsWrapper,
  UserSelectorStyling,
  UserTitleMobileWrapper,
  UserWalletActionButtonWrapper,
} from '../../atom/users-page-redesign-styles'
import { AlertError } from '../../components/error'
import { PlansRedesign } from '../../components/plans/plans-redesign'
import { MultiTenantSelector } from '../../components/redesign/multiple-tenant-selector'
import { MultiRfidSelector } from '../../components/redesign/multiple-rfid-selector'
import { NoIconSearch } from '../../components/redesign/no-icon-search'
import { UserSelectorNoTagRedesign } from '../../components/selector/user-selector-no-tag'
import { SubscriptionsRedesign } from '../../components/subscriptions/subscriptions-redesign'
import { useUserTranslation } from '../../hooks/translation/useUserTranslation'
import { SwtchError } from '../../models/error'
import { PaginationMeta } from '../../models/pagination'
import { TenantRef } from '../../models/tenant'
import {
  AugmentedUser,
  DashboardAccessType,
  isManagerToTenants,
  toAugmentedUser,
  User,
  UserInviteSuccess,
  UserWallet,
} from '../../models/user'
import { formatRfid } from '../../helpers/formatRfid'
import { FindTenants } from '../../services/data-provider/tenants'
import { GetManageUserByPersonId, GetUserInvitations, GetUsers } from '../../services/data-provider/users'
import { useAppState } from '../../state'
import { UserRFIDForm } from './modal/user-rfid-form'
import { UserEditForm } from './user-edit-redesign'
import { UserInvitationRedesign } from './user-invitation-redesign'
import UsersCollapse from './users-collapse'
import UserCloneForm from './modal/user-clone-form'
import { UserCloneSVG } from 'assets/svg/userClone'
import { userWalletDisabledSVG, userWalletSVG } from 'assets/svg/userWallet'
import { Links } from 'components/routes/paths'
import { useHistory } from 'react-router'
import { QuestionIcon } from 'assets/svg/questionMark'
import { CustomDateRangePicker } from 'components/redesign/datepicker'
import { useCalendarTransactions } from 'hooks/translation/useCalendarTranslation'

export const UsersRedesignBasePage: React.FC = () => {
  document.querySelector('body')?.classList.add('redesignActive')
  const responsiveHeaderTitleTabletAndLaptop = useMediaQuery({ maxWidth: 1233, minWidth: 0 })
  const showFullInviteButtonTabletAndLaptop = useMediaQuery({ maxWidth: 1233, minWidth: 856 })
  const showFullInviteButtonMobileAndTablet = useMediaQuery({ maxWidth: 758, minWidth: 0 })
  const responsiveLineBreak = useMediaQuery({ maxWidth: 1233, minWidth: 993 })
  const responsiveSearchButtonForMobile = useMediaQuery({ maxWidth: 492, minWidth: 0 })
  const showCollapsedTable = useMediaQuery({ maxWidth: 767, minWidth: 0 })
  const {
    currentUser,
    selectedTenant,
    IsLaptop,
    IsDesktop,
    IsTablet,
    IsMobile,
    isMockUpEnabled,
    language,
  } = useAppState()
  const [tenants, setTenants] = useState<TenantRef[]>([])
  /** Admin Users vs Invited tabs keep separate site filters so `tenant_id` is not shared across APIs. */
  const [usersTabTenants, setUsersTabTenants] = useState<TenantRef[]>([])
  const [invitedTabTenants, setInvitedTabTenants] = useState<TenantRef[]>([])
  const [users, setUsers] = useState<AugmentedUser[]>([])
  const [invitations, setInvitations] = useState<AugmentedUser[]>([])
  /** List filters are per tab so Invited Users date/search does not leak into GetUsers. */
  const [usersSearchTerm, setUsersSearchTerm] = useState('')
  const [invitedSearchTerm, setInvitedSearchTerm] = useState('')
  const [usersStartDate, setUsersStartDate] = useState<Moment | undefined>(undefined)
  const [usersEndDate, setUsersEndDate] = useState<Moment | undefined>(undefined)
  const [invitedStartDate, setInvitedStartDate] = useState<Moment | undefined>(undefined)
  const [invitedEndDate, setInvitedEndDate] = useState<Moment | undefined>(undefined)
  const [selectedUser, setSelectedUser] = useState<AugmentedUser>()
  const [editUserFetchLoading, setEditUserFetchLoading] = useState(false)
  const [cloneFetchLoading, setCloneFetchLoading] = useState(false)
  const [rfidFetchLoading, setRfidFetchLoading] = useState(false)
  const userEditCacheRef = useRef<Map<string, AugmentedUser>>(new Map())
  const staleUserDetailIdsRef = useRef<Set<string>>(new Set())
  const [loading, setLoading] = useState(false)
  const [usersPagination, setUsersPagination] = useState<PaginationMeta>()
  const [invitationsPagination, setInvitationsPagination] = useState<PaginationMeta>()
  //const [currentPage, setCurrentPage] = useState(1) //Commented out in case need in the future
  const [showInvitationForm, setShowInvitationForm] = useState(false)
  const [showEditUserNameForm, setEditUserNameForm] = useState(false)
  const [showRFIDForm, setShowRFIDForm] = useState(false)
  const [showCloneForm, setShowCloneForm] = useState(false)
  const [error, setError] = useState<SwtchError>()
  const [currentAugmentedUser, setCurrentAugmentedUser] = useState<AugmentedUser>()
  const [tab, setTab] = useState<'users' | 'invitedUsers' | 'plans' | 'subscriptions'>('users')
  const [createPlanForm, setShowCreatePlanForm] = useState(false)
  const [showInviteForm, setShowInviteForm] = useState(false)
  const [subscriptionEmailSearch, setSubscriptionEmailSearch] = useState<UserWallet | undefined>()
  const [searchSubscriptions, setSearchSubscriptions] = useState(false)
  const [rfid, setRfid] = useState<string>('')
  const { calendarRanges } = useCalendarTransactions()

  const scopeTenants: TenantRef[] =
    currentUser?.role === 'admin'
      ? tab === 'invitedUsers'
        ? invitedTabTenants
        : usersTabTenants
      : tenants
  const {
    emailTxt,
    driverInfoTxt,
    driverTxt,
    inviteUser,
    tenantsTxt,
    createPlanText,
    subscribeUserText,
    userText,
    noAccessText,
    roleTxt,
    plansText,
    subscriptionsText,
    guestUserText,
    manageRFIDText,
    cloneUserText,
    editUserInfoText,
    userWalletText,
    userWalletDisabledText,
    emailTooltipText,
    invitedUserEmailTooltipText,
    registrationDateText,
    lastTransactionText,
    walletBalanceText,
  } = useUserTranslation()
  const isDesktop = IsDesktop()
  const isTablet = IsTablet()
  const isMobile = IsMobile()
  const isLaptop = IsLaptop()
  const { siteLabelCleaner } = useSelector()
  const history = useHistory()

  const formatDate = (value?: string | Date | null) =>
    value ? dayjs(value).format('YYYY/MM/DD') : ''

  const formatDateTime = (value?: string | Date | null) =>
    value ? dayjs(value).format('YYYY/MM/DD HH:mm') : ''

  const handleChangeDate = (dates: Moment[]) => {
    const isInvitedTab = currentUser?.role === 'admin' && tab === 'invitedUsers'
    if (!dates || dates.length < 2) {
      if (isInvitedTab) {
        setInvitedStartDate(undefined)
        setInvitedEndDate(undefined)
      } else {
        setUsersStartDate(undefined)
        setUsersEndDate(undefined)
      }
      return
    }
    if (isInvitedTab) {
      setInvitedStartDate(dates[0])
      setInvitedEndDate(dates[1])
    } else {
      setUsersStartDate(dates[0])
      setUsersEndDate(dates[1])
    }
  }

  // normalize comma-separated multi-value input: alphanumeric only, uppercase
  const parseMultiValue = (value: string) =>
    value
      .split(',')
      .map((v) => formatRfid(v.trim()))
      .filter(Boolean)
      .join(',')

  const fetchUsers = (page = 1) => {
    setLoading(true)
    setError(undefined)
    // alert('fetchUsers')
    let pageNumber = 1
    //To handle event getting passed from Button onClick
    if (typeof page === 'number') {
      pageNumber = page
    }

    const showCurrentUserDataAtTop =
      !usersSearchTerm && currentUser?.accesses && isManagerToTenants(currentUser.accesses)
    const tenantList = currentUser?.role === 'admin' ? usersTabTenants : tenants
    if (tenantList.length === 0) {
      if (currentUser?.role === 'admin') {
        GetUsers(pageNumber, isMockUpEnabled, usersSearchTerm, undefined, undefined, usersStartDate, usersEndDate, rfid)
          .then((usersResp) => {
            setUsers(usersResp.data)
            setUsersPagination({
              ...usersResp.pagination,
              totalEntries: usersResp.pagination.totalEntries,
              perPage: usersResp.pagination.perPage,
            })
          })
          .catch((error) => setError(error))
          .finally(() => setLoading(false))
      } else {

        FindTenants(currentUser, false, isMockUpEnabled)
          .then((tenantsForUser) => {
            Promise.all([
              ...(showCurrentUserDataAtTop
                ? [GetUsers(1, isMockUpEnabled, currentUser?.email, undefined, undefined, usersStartDate, usersEndDate, rfid)]
                : []),
              GetUsers(
                pageNumber,
                isMockUpEnabled,
                usersSearchTerm,
                selectedTenant,
                tenantsForUser,
                usersStartDate,
                usersEndDate,
                rfid,
              ),
            ])
              .then((responses) => {
                const currentUserResp = showCurrentUserDataAtTop ? responses[0] : undefined
                const usersResp = showCurrentUserDataAtTop ? responses[1] : responses[0]

                setUsers(
                  usersResp.data.filter(({ email }) =>
                    showCurrentUserDataAtTop ? email !== currentUser?.email : true,
                  ),
                )
                setUsersPagination({
                  ...usersResp.pagination,
                  perPage: usersResp.pagination.perPage,
                  totalEntries: usersResp.pagination.totalEntries,
                })
              })
              .catch((error) => {
                setError(error)
              })
              .finally(() => setLoading(false))
          })
          .catch((err: SwtchError) => message.error(err.description))
      }
    } else {
      Promise.all([
        ...(showCurrentUserDataAtTop
          ? [GetUsers(1, isMockUpEnabled, currentUser?.email, undefined, undefined, usersStartDate, usersEndDate, rfid)]
          : []),
        GetUsers(
          pageNumber,
          isMockUpEnabled,
          usersSearchTerm,
          selectedTenant,
          tenantList,
          usersStartDate,
          usersEndDate,
          rfid,
        ),
      ])
        .then((responses) => {
          const currentUserResp = showCurrentUserDataAtTop ? responses[0] : undefined
          const usersResp = showCurrentUserDataAtTop ? responses[1] : responses[0]

          setUsers(
            usersResp.data.filter(({ email }) => (showCurrentUserDataAtTop ? email !== currentUser?.email : true)),
          )
          setUsersPagination({
            ...usersResp.pagination,
            perPage: usersResp.pagination.perPage,
            totalEntries: usersResp.pagination.totalEntries,
          })
        })
        .catch((error) => setError(error))
        .finally(() => setLoading(false))
    }
  }

  const fetchInvitations = (page = 1) => {
    setLoading(true)
    setError(undefined)

    let pageNumber = 1
    // To handle event getting passed from Button onClick
    if (typeof page === 'number') {
      pageNumber = page
    }

    const showCurrentUserDataAtTop =
      !invitedSearchTerm && currentUser?.accesses && isManagerToTenants(currentUser.accesses)

    const tenantList = currentUser?.role === 'admin' ? invitedTabTenants : tenants

    // Keep behavior consistent with `fetchUsers()`:
    // - In the `admin && tenantList.length === 0` branch, `fetchUsers()` does NOT prepend current user.
    if (tenantList.length === 0) {
      if (currentUser?.role === 'admin') {
        GetUserInvitations(
          pageNumber,
          isMockUpEnabled,
          invitedSearchTerm,
          undefined,
          undefined,
          invitedStartDate,
          invitedEndDate,
        )
          .then((invitationsResp) => {
            setInvitations(invitationsResp.data)
            setInvitationsPagination(invitationsResp.pagination)
          })
          .catch((error) => setError(error))
          .finally(() => setLoading(false))
        return
      }

      FindTenants(currentUser, false, isMockUpEnabled)
        .then((tenantsForUser) => {
          Promise.all([
            showCurrentUserDataAtTop
              ? GetUsers(1, isMockUpEnabled, currentUser?.email, undefined, undefined, undefined, undefined, undefined)
              : Promise.resolve(undefined),
            GetUserInvitations(
              pageNumber,
              isMockUpEnabled,
              invitedSearchTerm,
              selectedTenant,
              tenantsForUser,
              invitedStartDate,
              invitedEndDate,
            ),
          ])
            .then(([currentUserResp, invitationsResp]) => {
              setInvitations([...(currentUserResp?.data || []), ...invitationsResp.data])
              setInvitationsPagination(invitationsResp.pagination)
            })
            .catch((error) => setError(error))
            .finally(() => setLoading(false))
        })
        .catch((err: SwtchError) => message.error(err.description))

      return
    }

    Promise.all([
      ...(showCurrentUserDataAtTop
        ? [
            GetUsers(1, isMockUpEnabled, currentUser?.email, undefined, undefined, undefined, undefined, undefined),
          ]
        : []),
      GetUserInvitations(
        pageNumber,
        isMockUpEnabled,
        invitedSearchTerm,
        selectedTenant,
        tenantList,
        invitedStartDate,
        invitedEndDate,
      ),
    ])
      .then((responses) => {
        const invitationsResp = showCurrentUserDataAtTop ? responses[1] : responses[0]
        const currentUserResp = showCurrentUserDataAtTop ? responses[0] : undefined

        setInvitations([...(currentUserResp?.data || []), ...invitationsResp.data])
        setInvitationsPagination(invitationsResp.pagination)
      })
      .catch((error) => setError(error))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    fetchUsers()
  }, [])

  useEffect(() => {
    // Filter/scope changed; invalidate cached user details used by modals.
    userEditCacheRef.current.clear()
    staleUserDetailIdsRef.current.clear()
  }, [
    usersSearchTerm,
    invitedSearchTerm,
    tenants,
    usersTabTenants,
    invitedTabTenants,
    usersStartDate,
    usersEndDate,
    invitedStartDate,
    invitedEndDate,
    rfid,
  ])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [usersPagination, invitationsPagination])

  const tenantScope: TenantRef[] = selectedTenant
    ? [
        {
          id: selectedTenant.id,
          name: selectedTenant.name,
          displayName: selectedTenant.displayName,
          combineName: selectedTenant.combineName,
        },
      ]
    : scopeTenants
  const hasTenantScope = tenantScope !== undefined

  useEffect(() => {
    if (selectedTenant) {
      const scoped = [
        {
          id: selectedTenant.id,
          name: selectedTenant.name,
          displayName: selectedTenant.displayName,
          combineName: selectedTenant.combineName,
        },
      ]
      if (currentUser?.role === 'admin') {
        setUsersTabTenants(scoped)
        setInvitedTabTenants(scoped)
      } else {
        setTenants(scoped)
      }
    }
    let currentAugmentedUserTemp = currentUser && toAugmentedUser(currentUser)
    setCurrentAugmentedUser(currentAugmentedUserTemp)
  }, [])

  const handleUserInvited = (newUser: User | UserInviteSuccess, isMockUpEnabled: boolean) => {
    setShowInvitationForm(false)
    if (isMockUpEnabled && 'role' in newUser) {
      return setUsers([...users, toAugmentedUser(newUser)])
    }
    if (tab === 'invitedUsers') {
      return fetchInvitations(1)
    }
    return fetchUsers(1)
  }

  const onPaginationChange = (page: number, pageSize?: number) => {
    if (tab === 'invitedUsers') {
      fetchInvitations(page)
    } else {
      fetchUsers(page)
    }
  }

  /** Search / Enter: users list vs invitations depends on active admin tab (and matches pagination). */
  const runSearchForActiveList = () => {
    if (currentUser?.role === 'admin' && tab === 'invitedUsers') {
      return fetchInvitations(1)
    }
    return fetchUsers(1)
  }

  const renderDriverInfoText = () => {
    let temp = undefined
    if (scopeTenants && scopeTenants.length === 1) {
      temp = siteLabelCleaner(scopeTenants[0].combineName)
    } else if (scopeTenants && scopeTenants.length > 1) {
      temp = `${scopeTenants.length} ${tenantsTxt}`
    }
    return `${driverInfoTxt} ${temp}`
  }

  /** Full user for edit/clone/RFID: cache + GET `/v2/manage/users/:id` when needed. */
  const fetchUserForModal = async (rowUser: AugmentedUser): Promise<AugmentedUser> => {
    if (rowUser.recordType && rowUser.recordType !== 'Person') {
      return rowUser
    }

    const id = (rowUser.id || '').trim()
    if (!id) {
      throw new Error('User id is missing')
    }

    const cached = userEditCacheRef.current.get(id)
    const needsApi = !cached || staleUserDetailIdsRef.current.has(id)
    if (!needsApi && cached) {
      return cached
    }

    const fullUser = await GetManageUserByPersonId(id)
    userEditCacheRef.current.set(id, fullUser)
    staleUserDetailIdsRef.current.delete(id)
    return fullUser
  }

  const openUserForEdit = async (rowUser: AugmentedUser) => {
    if (rowUser.recordType && rowUser.recordType !== 'Person') {
      setSelectedUser(rowUser)
      setEditUserNameForm(true)
      return
    }

    const id = rowUser.id
    const cached = userEditCacheRef.current.get(id)
    const needsApi = !cached || staleUserDetailIdsRef.current.has(id)

    if (!needsApi && cached) {
      setSelectedUser(cached)
      setEditUserNameForm(true)
      return
    }

    setEditUserFetchLoading(true)
    setEditUserNameForm(true)
    setSelectedUser(undefined)
    try {
      const fullUser = await fetchUserForModal(rowUser)
      setSelectedUser(fullUser)
    } catch (err) {
      const description = (err as SwtchError)?.description
      message.error(description || (err as Error)?.message || 'Could not load user')
      setEditUserNameForm(false)
    } finally {
      setEditUserFetchLoading(false)
    }
  }

  const handleModifyBtn = (user: AugmentedUser) => {
    void openUserForEdit(user)
  }

  const handleRFIDBtn = async (user: AugmentedUser) => {
    if (user.recordType && user.recordType !== 'Person') {
      setSelectedUser(user)
      setShowRFIDForm(true)
      return
    }

    const cached = userEditCacheRef.current.get(user.id)
    const needsApi = !cached || staleUserDetailIdsRef.current.has(user.id)
    if (!needsApi && cached) {
      setSelectedUser(cached)
      setShowRFIDForm(true)
      return
    }

    setRfidFetchLoading(true)
    setShowRFIDForm(true)
    setSelectedUser(undefined)
    try {
      const fullUser = await fetchUserForModal(user)
      setSelectedUser(fullUser)
    } catch (err) {
      const description = (err as SwtchError)?.description
      message.error(description || (err as Error)?.message || 'Could not load user')
      setShowRFIDForm(false)
    } finally {
      setRfidFetchLoading(false)
    }
  }

  const handleCloneBtn = async (user: AugmentedUser) => {
    if (user.recordType && user.recordType !== 'Person') {
      setSelectedUser(user)
      setShowCloneForm(true)
      return
    }

    const cached = userEditCacheRef.current.get(user.id)
    const needsApi = !cached || staleUserDetailIdsRef.current.has(user.id)
    if (!needsApi && cached) {
      setSelectedUser(cached)
      setShowCloneForm(true)
      return
    }

    setCloneFetchLoading(true)
    setShowCloneForm(true)
    setSelectedUser(undefined)
    try {
      const fullUser = await fetchUserForModal(user)
      setSelectedUser(fullUser)
    } catch (err) {
      const description = (err as SwtchError)?.description
      message.error(description || (err as Error)?.message || 'Could not load user')
      setShowCloneForm(false)
    } finally {
      setCloneFetchLoading(false)
    }
  }

  const handleAddUser = () => {
    let currentAugmentedUserTemp = currentUser && toAugmentedUser(currentUser)
    setCurrentAugmentedUser(currentAugmentedUserTemp)
    // setAddUserToTenant(currentAugmentedUserTemp)
    setShowInvitationForm(true)
  }

  const renderAccess = (user: AugmentedUser) => {
    log('user', user)
    const { tenantPermission } = user
    const permissions = Object.values(tenantPermission)

    let access: DashboardAccessType | 'Multi' | undefined
    if (scopeTenants.length === 1) {
      access = permissions
        .filter((permission) => permission.id === scopeTenants[0].id)
        .map((permission) => permission.access)[0]
    }
    if (access === undefined) {
      access = 'none'
    }

    const style = user?.recordType === 'UserInvitation' ? { color: theme.colors.darkGray } : {}

    if (access === 'none') {
      return <div style={style}>None</div>
    } else if (access === 'manager') {
      return <div style={style}>Manager</div>
    } else if (access === 'viewer') {
      return <div style={style}>Viewer</div>
    } else if (access === 'Multi') {
      return <div style={style}>Multi</div>
    }
  }

  const UserWalletActionButton = ({ user }: { user: any }) => {
    const userHasWallet = user?.hasWallet
    console.log('userHasWallet', userHasWallet)
    if (userHasWallet) {
      return (
        <ButtonModal
          type="link"
          style={{ padding: '10px' }}
          icon={
            <Tooltip
              overlayInnerStyle={{ borderRadius: '5px', padding: '7px' }}
              placement="top"
              title={userWalletText}
            >
              {userWalletSVG}
            </Tooltip>
          }
          click={() => {
            history.push(Links.userWallet({ userId: user.id }))
          }}
        />
      )
    }

    return (
      <UserWalletActionButtonWrapper>
        <Tooltip
          overlayInnerStyle={{ borderRadius: '5px', padding: '7px' }}
          placement="top"
          title={userWalletDisabledText}
        >
          {userWalletDisabledSVG}
        </Tooltip>
      </UserWalletActionButtonWrapper>
    )
  }

  const columns: any = [
    {
      title: (
        <Row align="middle" style={{ gap: '8px' }}>
          <span>{emailTxt}</span>
          <Tooltip
            title={
              <span style={{ whiteSpace: 'pre-line' }}>
                {tab === 'invitedUsers' ? invitedUserEmailTooltipText : emailTooltipText}
              </span>
            }
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <QuestionIcon />
            </div>
          </Tooltip>
        </Row>
      ),
      render: (user: User) => {
        if (user?.recordType === 'UserInvitation') {
          return <div style={{ color: theme.colors.darkGray }}>{user.email}</div>
        }
        return user.email
      },
      width: '32%',
    },
    {
      title: tab === 'invitedUsers' ? 'Invited By' : userText,
      render: (user: any) => {
        if (tab === 'invitedUsers') {
          return user.invitedBy
        }
        if (user?.recordType === 'UserInvitation') {
          return (
            <div style={{ color: theme.colors.darkGray }}>
              {user.guest ? guestUserText : user.name}
            </div>
          )
        }
        if (user.guest) {
          return guestUserText
        }
        return user.name
      },
      width: '18%',
    },
    {
      title: (
        <Row align="middle" style={{ gap: 8 }}>
          <span>{tab === 'invitedUsers' ? 'Invitation Date' : registrationDateText}</span>
          <Tooltip
            title={
              tab === 'invitedUsers'
                ? 'The date the user received an invitation to create their account.'
                : 'The date the user created their account.'
            }
          >
            <QuestionIcon />
          </Tooltip>
        </Row>
      ),
      key: 'registrationDate',
      render: (user: any) => {
        const date = user.createdAt
        const value = formatDate(date)

        if (!value) {
          return <div>—</div>
        }

        const style = user?.recordType === 'UserInvitation' ? { color: theme.colors.darkGray } : {}
        return <div style={{ ...style, whiteSpace: 'nowrap' }}>{value}</div>
      },
      width: '16%',
    },
    {
      title: (
        <Row align="middle" style={{ gap: 8 }}>
          <span>{lastTransactionText}</span>
          <Tooltip title="The date and time of the user's most recent charging activity.">
            <QuestionIcon />
          </Tooltip>
        </Row>
      ),
      key: 'lastTransaction',
      render: (user: any) => {
        const lastDisconnectedAt = user.lastDisconnectedAt
        if (!lastDisconnectedAt) return <div />
        const dt = new Date(lastDisconnectedAt)
        const dateString = dt.toLocaleDateString('en-CA')
        const timeString = dt.toLocaleTimeString('en-US', {
          hour12: false,
          timeZoneName: 'short',
        })
        const [time, zone] = timeString.split(' ')
        const style = user?.recordType === 'UserInvitation' ? { color: theme.colors.darkGray } : {}
        return (
          <div style={{ ...style, lineHeight: '18px' }}>
              <DateTimeV2 alignLeft date={lastDisconnectedAt} timezone={zone} />
          </div>
        )
      },
      width: '16%',
    },
    {
      title: (
        <Row align="middle" style={{ gap: 8 }}>
          <span>{walletBalanceText}</span>
          <Tooltip title="User’s current wallet balance, with the flag shown automatically based on the wallet currency.">
            <QuestionIcon />
          </Tooltip>
        </Row>
      ),
      key: 'walletBalance',
      render: (user: any) => {
        const amount = user.walletAmount
        const country = user.walletCountry

        if (!amount) return <div />
        if (!country) return <div />

        let flag: string | null = null
        if (country === 'CA') flag = '🇨🇦'
        if (country === 'US') flag = '🇺🇸'

        const style = user?.recordType === 'UserInvitation' ? { color: theme.colors.darkGray } : {}

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, ...style }}>
            {flag && <span>{flag}</span>}
            <span>{amount}</span>
          </div>
        )
      },
      width: '14%',
    },
  ]

  log('hasTenantScope', hasTenantScope)
  log('tenants', scopeTenants)
  if (hasTenantScope && scopeTenants && scopeTenants.length === 1) {
    columns.push(
      {
        title: (
          <>
            {driverTxt}
            <Tooltip
              overlayInnerStyle={{ borderRadius: '5px', width: '172px', padding: '7px' }}
              placement="top"
              title={renderDriverInfoText()}
            >
              <DriverSvgSpacing>{<QuestionIcon />}</DriverSvgSpacing>
            </Tooltip>
          </>
        ),
        key: 'driver',
        className: 'user-driver',
        render: ({ accessPlans, recordType }: AugmentedUser) => {
          if (!tenantScope) {
            return <></>
          }
          const currentUserTenantAccesses = scopeTenants.map(({ id }) => Number(id))
          const accessPlanCurrentUserHasAccess = accessPlans?.filter(({ tenant_id }) =>
            currentUserTenantAccesses.includes(tenant_id),
          )
          if (accessPlanCurrentUserHasAccess?.length) {
            return (
              <div>
                <span style={recordType === 'UserInvitation' ? { color: theme.colors.darkGray } : {}}>
                  {accessPlanCurrentUserHasAccess.map((plan) => plan.name).join(', ')}
                </span>
              </div>
            )
          } else {
            return <div style={{ color: theme.colors.darkGray }}>{noAccessText}</div>
          }
        },
        width: '10%',
      },
      Table.EXPAND_COLUMN,
      {
        title: <div>{roleTxt}</div>,
        key: 'tenantAccess',
        render: (user: AugmentedUser) => renderAccess(user),
      },
      {
        title: '',
        key: 'action',
        render: (user: AugmentedUser) =>
          user.recordType === 'Person' && (
            <EditButtonWrapper>
              <ButtonModal
                type="link"
                style={{ padding: '10px' }}
                icon={
                  <Tooltip
                    overlayInnerStyle={{ borderRadius: '5px', padding: '7px' }}
                    placement="top"
                    title={editUserInfoText}
                  >
                    {editGreySVG}
                  </Tooltip>
                }
                click={() => handleModifyBtn(user)}
              />
              {currentUser?.role === 'admin' || (currentUser?.accesses && isManagerToTenants(currentUser?.accesses)) ? (
                <ButtonModal
                  type="link"
                  style={{ padding: '10px' }}
                  icon={
                    <Tooltip
                      overlayInnerStyle={{ borderRadius: '5px', padding: '7px' }}
                      placement="top"
                      title={cloneUserText}
                    >
                      <UserCloneSVG />
                    </Tooltip>
                  }
                  click={() => handleCloneBtn(user)}
                />
              ) : (
                <div style={{ width: '47px' }} />
              )}
              {currentUser?.role === 'admin' && <UserWalletActionButton user={user} />}
              {currentUser?.role === 'admin' ||
              (currentUser?.accesses &&
                isManagerToTenants(currentUser?.accesses) &&
                user.email === currentUser.email) ? (
                <ButtonModal
                  type="link"
                  style={{ padding: '10px' }}
                  icon={
                    <Tooltip
                      overlayInnerStyle={{ borderRadius: '5px', padding: '7px' }}
                      placement="top"
                      title={manageRFIDText}
                    >
                      {creditCardSVG}
                    </Tooltip>
                  }
                  click={() => handleRFIDBtn(user)}
                />
              ) : (
                <div style={{ width: '47px' }} />
              )}
            </EditButtonWrapper>
          ),
      },
    )
  } else if (currentUser?.role === 'admin' || currentUser?.role === 'user') {
    columns.push({
      title: '',
      key: 'action',
      render: (user: AugmentedUser) =>
        user.recordType === 'Person' && (
          <EditButtonWrapper>
            <ButtonModal
              type="link"
              style={{ padding: '10px' }}
              icon={
                <Tooltip
                  overlayInnerStyle={{ borderRadius: '5px', padding: '7px' }}
                  placement="top"
                  title={editUserInfoText}
                >
                  {editGreySVG}
                </Tooltip>
              }
              click={() => handleModifyBtn(user)}
            />
            {currentUser?.role === 'admin' || (currentUser?.accesses && isManagerToTenants(currentUser?.accesses)) ? (
              <ButtonModal
                style={{ padding: '10px' }}
                type="link"
                icon={
                  <Tooltip
                    overlayInnerStyle={{ borderRadius: '5px', padding: '7px' }}
                    placement="top"
                    title={cloneUserText}
                  >
                    <UserCloneSVG />
                  </Tooltip>
                }
                click={() => handleCloneBtn(user)}
              />
            ) : (
              <div style={{ width: '47px' }} />
            )}
            {currentUser?.role === 'admin' && <UserWalletActionButton user={user} />}
            {currentUser?.role === 'admin' ||
            (currentUser?.accesses && isManagerToTenants(currentUser?.accesses) && user.email === currentUser.email) ? (
              <ButtonModal
                type="link"
                style={{ padding: '10px' }}
                icon={
                  <Tooltip
                    overlayInnerStyle={{ borderRadius: '5px', padding: '7px' }}
                    placement="top"
                    title={manageRFIDText}
                  >
                    {creditCardSVG}
                  </Tooltip>
                }
                click={() => handleRFIDBtn(user)}
              />
            ) : (
              <div style={{ width: '47px' }} />
            )}
          </EditButtonWrapper>
        ),
    })
  }
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const v = e.target.value
    if (currentUser?.role === 'admin' && tab === 'invitedUsers') {
      setInvitedSearchTerm(v)
    } else {
      setUsersSearchTerm(v)
    }
  }

  const handleRfidChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const cleaned = parseMultiValue(e.target.value)
    setRfid(cleaned)
  }

  const handleSubscriptionEmailChange = (user: UserWallet | undefined) => setSubscriptionEmailSearch(user)

  const onSubmitSubscriptionSearch = () => setSearchSubscriptions(!searchSubscriptions)

  const listCalendarStart =
    currentUser?.role === 'admin' && tab === 'invitedUsers' ? invitedStartDate : usersStartDate
  const listCalendarEnd =
    currentUser?.role === 'admin' && tab === 'invitedUsers' ? invitedEndDate : usersEndDate

  const calendar = (
    <CustomDateRangePicker
      startDate={listCalendarStart}
      endDate={listCalendarEnd}
      onChangeDate={handleChangeDate}
      calendarRanges={calendarRanges}
      disabled={loading}
    />
  )

  const renderTitle = () => {
    if (currentUser?.role !== 'admin') {
      return (
        <Row
          gutter={[8, 8]}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            backgroundColor: '#fafafa',
            padding: '20px 4px 21px',
          }}
        >
          <div style={{ display: 'flex' }}>
            <MultiTenantStyling>
              <MultiTenantSelector
                longerWidth={true}
                isCombineRight
                noChangeOnFocus={true}
                onOptionsChange={(tenants) => setTenants(tenants)}
                onClear={() => setTenants([])}
                dropdownClassName={'not-in-modal-selector'}
              />
            </MultiTenantStyling>
            <LineBreak />
            <UserSelectorStyling>
              <SearchButtonContainer>
                <NoIconSearch
                  svg={<></>}
                  placeholder={emailTxt}
                  value={usersSearchTerm}
                  onChange={handleEmailChange}
                  onPressEnter={fetchUsers}
                />
                <SearchButtonWrapper>
                  <ButtonIcon
                    icon={magnifyBlackSVG}
                    loading={loading}
                    disabled={loading}
                    fetchData={fetchUsers}
                    withinSearchBar
                  />
                </SearchButtonWrapper>
              </SearchButtonContainer>
            </UserSelectorStyling>
          </div>
          <Space wrap size={[8, 8]}>
            {calendar}
          </Space>
          <Space>
            <InviteButtonWrapper>
              <ButtonModal size="large" click={handleAddUser} icon={plusSVG} text={inviteUser} type="primary" />
            </InviteButtonWrapper>
          </Space>
        </Row>
      )
    }
  }

  const renderColumns = (): any => {
    let currentAugmentedUser = currentUser && toAugmentedUser(currentUser)
    const tenantAccesses =
      currentAugmentedUser?.tenantPermission && Object.values(currentAugmentedUser?.tenantPermission)
    const isManager = tenantAccesses?.some((item) => item.access === 'manager')

    const listSearchTerm = tab === 'invitedUsers' ? invitedSearchTerm : usersSearchTerm
    if ((currentAugmentedUser?.isAdmin || isManager) && !listSearchTerm) {
      return columns
        .filter((col: any) => col.key !== 'manageraddusertotenants')
        .filter((col: any) => col.key !== 'addusertotenants')
    }
    return listSearchTerm ? columns : columns.filter((col: any) => col.key !== 'addusertotenants')
  }

  const [loadedColumns, setLoadedColumns] = useState<any>(columns)
  const [expandableRowKeys, setExpandableRowKeys] = useState<{ [dataKey: string]: boolean }>({})
  const [reload, setReload] = useState(1)

  useEffect(() => {
    const columns = renderColumns()
    setLoadedColumns(columns)
  }, [
    currentUser,
    usersSearchTerm,
    invitedSearchTerm,
    tenants,
    usersTabTenants,
    invitedTabTenants,
    language,
    tab,
  ])

  useEffect(() => {
    if (loadedColumns.length > 0 && users.length > 0 && !loading) {
      users.forEach((record) => {
        rowExpandable(record as any)
      })
      setReload(reload + 1)
    }
  }, [loadedColumns, users, loading])

  const renderTitleMobile = () => {
    return (
      <>
        <UserTitleMobileWrapper isAdmin={currentUser?.role === 'admin'}>
          <MultiTenantMobileStyling>
            <MultiTenantSelector
              longerWidth={true}
              isCombineRight
              noChangeOnFocus={true}
              onOptionsChange={(opts) =>
                currentUser?.role === 'admin'
                  ? tab === 'invitedUsers'
                    ? setInvitedTabTenants(opts)
                    : setUsersTabTenants(opts)
                  : setTenants(opts)
              }
              onClear={() =>
                currentUser?.role === 'admin'
                  ? tab === 'invitedUsers'
                    ? setInvitedTabTenants([])
                    : setUsersTabTenants([])
                  : setTenants([])
              }
              dropdownClassName={'not-in-modal-selector'}
            />
          </MultiTenantMobileStyling>
        </UserTitleMobileWrapper>
        <div style={{ display: 'flex' }}>
          <MultiTenantMobileStyling>
            <NoIconSearch
              svg={<></>}
              placeholder={emailTxt}
              value={
                currentUser?.role === 'admin' && tab === 'invitedUsers'
                  ? invitedSearchTerm
                  : usersSearchTerm
              }
              onChange={handleEmailChange}
              onPressEnter={runSearchForActiveList}
            />
          </MultiTenantMobileStyling>
          <MultiTenantMobileStyling>
            {renderMultiRfidSelector}
          </MultiTenantMobileStyling>
        </div>
        <div style={{ marginBottom: '10px' }}>{calendar}</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
          <Button
            style={{ width: `${responsiveSearchButtonForMobile ? '80%' : '90%'}` }}
            type="primary"
            onClick={() => {
              runSearchForActiveList()
            }}
          >
            {magnifyBlackSVG}
            <ButtonTextSpacing>
              <span className="paragraph-02-regular">SEARCH</span>
            </ButtonTextSpacing>
          </Button>
          <InviteButtonCircleWrapper>
            <ButtonIconCircle icon={plusSVG} fetchData={handleAddUser} disabled={false} loading={false} size="large" />
          </InviteButtonCircleWrapper>
        </div>
      </>
    )
  }

  const renderTitleTablet = () => {
    let padding = ''
    if (currentUser?.role === 'admin') {
      padding = '0px 4px 21px'
    } else {
      padding = '20px 4px 21px'
    }

    return (
      <Row
        gutter={[8, 8]}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#fafafa',
          padding: padding,
        }}
      >
        <div style={{ display: 'flex' }}>
          <MultiTenantStyling>
            <MultiTenantSelector
              longerWidth={true}
              isCombineRight
              noChangeOnFocus={true}
              onOptionsChange={(opts) =>
                currentUser?.role === 'admin'
                  ? tab === 'invitedUsers'
                    ? setInvitedTabTenants(opts)
                    : setUsersTabTenants(opts)
                  : setTenants(opts)
              }
              onClear={() =>
                currentUser?.role === 'admin'
                  ? tab === 'invitedUsers'
                    ? setInvitedTabTenants([])
                    : setUsersTabTenants([])
                  : setTenants([])
              }
            />
          </MultiTenantStyling>
          {responsiveLineBreak && <LineBreak isAdmin={currentUser?.role === 'admin'} />}
          <UserSelectorStyling tablet={true}>
              <NoIconSearch
                svg={<></>}
                placeholder={emailTxt}
                value={
                  currentUser?.role === 'admin' && tab === 'invitedUsers'
                    ? invitedSearchTerm
                    : usersSearchTerm
                }
                onChange={handleEmailChange}
                onPressEnter={runSearchForActiveList}
              />
          </UserSelectorStyling>
          <UserSelectorStyling tablet={true}>
            <SearchButtonContainer>
              {renderMultiRfidSelector}
              <SearchButtonWrapper>
                <ButtonIcon
                  icon={magnifyBlackSVG}
                  loading={loading}
                  disabled={loading}
                  fetchData={runSearchForActiveList}
                  withinSearchBar
                />
              </SearchButtonWrapper>
            </SearchButtonContainer>
          </UserSelectorStyling>
        </div>
        <Space wrap size={[8, 8]}>
          {calendar}
        </Space>
        {showFullInviteButtonTabletAndLaptop || showFullInviteButtonMobileAndTablet ? (
          <>
            <InviteButtonWrapper>
              <ButtonModal size="large" click={handleAddUser} icon={plusSVG} text={inviteUser} type="primary" />
            </InviteButtonWrapper>
          </>
        ) : (
          <InviteButtonCircleWrapper>
            <ButtonIconCircle icon={plusSVG} fetchData={handleAddUser} disabled={false} loading={false} size="large" />
          </InviteButtonCircleWrapper>
        )}
      </Row>
    )
  }

  const onExpand = (expanded: boolean, record: TenantRef) => {
    setExpandableRowKeys({ ...expandableRowKeys, [record.id]: expanded })
  }

  const rowExpandable = (record: TenantRef) => {
    if (Object.keys(expandableRowKeys).includes(`${record.id}`)) return true
    const rowElement = document.querySelector(`[data-row-key="${record.id}"]`)

    const currentRowHeight = (rowElement as HTMLElement)?.offsetHeight - 12 * 2 //considered padding
    const childElements = rowElement?.querySelectorAll('.custom-row-collapse-users-page > td > *')

    let expandable = false

    childElements?.forEach((el: Element) => {
      expandable = expandable || el.scrollHeight > currentRowHeight
    })
    return expandable
  }

  const usersTable = (view: 'users' | 'invitations' | 'all' = 'all') => {
    const tablePagination = view === 'invitations' ? invitationsPagination : usersPagination

    const dataForMobileOrTablet =
      currentUser?.role === 'admin' && view === 'invitations' ? invitations : users

    // Invited-only tab must render the table when empty so Ant Design can show "No data".
    // Combined `all` view only shows the invitations block when there are rows.
    const showInvitationsTable =
      (view === 'all' && !!invitations.length) || view === 'invitations'
    const showUsersTable = view === 'all' || view === 'users'

    return (
    <Row>
      <Col span={24}>
        <AlertError error={error} />
        <Col>
          {(isDesktop || isLaptop) && (
            <TableStyling usersPage>
              <TableWrapper userIsAdmin={currentUser?.role === 'admin'}>
                  {currentUser?.role === 'admin'
                    ? null
                    : responsiveHeaderTitleTabletAndLaptop
                    ? renderTitleTablet()
                    : renderTitle()}
                  {showInvitationsTable && (
                  <ResponsiveTable
                    loading={loading}
                    size="small"
                    className="expandable-table no-min-height-table"
                    pagination={false}
                    dataSource={invitations}
                    columns={loadedColumns}
                    expandable={
                      hasTenantScope && scopeTenants && scopeTenants.length === 1
                        ? {
                            onExpand,
                            expandedRowRender: () => <></>,
                            rowExpandable,
                            expandIcon: ({ expandable, expanded, record, onExpand }) => {
                              return expandable ? (
                                expanded ? (
                                  <MinusCircleOutlined onClick={(e) => onExpand(record, e)} />
                                ) : (
                                  <PlusCircleOutlined onClick={(e) => onExpand(record, e)} />
                                )
                              ) : undefined
                            },
                          }
                        : undefined
                    }
                    rowKey="id"
                  />
                )}
                {showUsersTable && (
                <ResponsiveTable
                  key={reload}
                  loading={loading}
                  size="small"
                  className="expandable-table"
                  pagination={{
                    position: ['bottomCenter'],
                    total: tablePagination?.totalEntries,
                    current: tablePagination?.currentPage,
                    pageSize: tablePagination?.perPage,
                    showSizeChanger: false,
                    onChange: onPaginationChange,
                  }}
                  dataSource={users}
                  columns={loadedColumns}
                  showHeader={!showInvitationsTable}
                  expandable={
                    hasTenantScope && scopeTenants && scopeTenants.length === 1
                      ? {
                          onExpand,
                          expandedRowRender: () => <></>,
                          rowExpandable,
                          expandIcon: ({ expandable, expanded, record, onExpand }) => {
                            return expandable ? (
                              expanded ? (
                                <MinusCircleOutlined onClick={(e) => onExpand(record, e)} />
                              ) : (
                                <PlusCircleOutlined onClick={(e) => onExpand(record, e)} />
                              )
                            ) : undefined
                          },
                        }
                      : undefined
                  }
                  rowClassName={(record) => expandableRowKeys[record.id] ? '' : 'custom-row-collapse-users-page'}
                  rowKey="id"
                />
                )}
              </TableWrapper>
            </TableStyling>
          )}
          {isTablet && (
            <>
              {showCollapsedTable ? (
                <>
                  {renderTitleMobile()}
                  <UsersCollapse
                    loading={false}
                    users={dataForMobileOrTablet}
                    pagination={tablePagination}
                    onPaginationChange={onPaginationChange}
                    tenants={scopeTenants}
                    setEditUserNameForm={setEditUserNameForm}
                    setSelectedUser={setSelectedUser}
                    onOpenUserEdit={openUserForEdit}
                  />
                </>
              ) : (
                <TableStyling usersPage>
                  <TableWrapper userIsAdmin={currentUser?.role === 'admin'}>
                    <ResponsiveTable
                      title={showFullInviteButtonMobileAndTablet ? renderTitleMobile : renderTitleTablet}
                      loading={loading}
                      size="small"
                      pagination={{
                        position: ['bottomCenter'],
                        total: tablePagination?.totalEntries,
                        current: tablePagination?.currentPage,
                        pageSize: tablePagination?.perPage,
                        showSizeChanger: false,
                        onChange: onPaginationChange,
                      }}
                      dataSource={dataForMobileOrTablet}
                      columns={renderColumns()}
                      rowKey="id"
                    />
                  </TableWrapper>
                </TableStyling>
              )}
            </>
          )}
          {isMobile && (
            <>
              {renderTitleMobile()}
              <UsersCollapse
                loading={false}
                users={dataForMobileOrTablet}
                pagination={tablePagination}
                onPaginationChange={onPaginationChange}
                tenants={scopeTenants}
                setEditUserNameForm={setEditUserNameForm}
                setSelectedUser={setSelectedUser}
                onOpenUserEdit={openUserForEdit}
              />
            </>
          )}
        </Col>
      </Col>
    </Row>
  )
  }

  const onFinishUserEdit = (closeModalFunction: () => void) => () => {
    const userId = selectedUser?.id
    closeModalFunction()
    if (userId) {
      staleUserDetailIdsRef.current.add(userId)
      userEditCacheRef.current.delete(userId)
    }
    return fetchUsers(1)
  }

  const closeRfidModal = () => {
    if (selectedUser?.id) {
      staleUserDetailIdsRef.current.add(selectedUser.id)
      userEditCacheRef.current.delete(selectedUser.id)
    }
    setShowRFIDForm(false)
  }

  const renderMultiRfidSelector = (
    <MultiRfidSelector
      loading={loading}
      longerWidth={false}
      onOptionsChange={(opts) => {
        // opts: array of RFID objects (e.g. { id, rfidNumber }) - normalize to alphanumeric uppercase
        const rfidValue = opts
          .map((r: any) => formatRfid(String(r.rfidNumber || '').trim()))
          .filter(Boolean)
          .join(',')

        setRfid(rfidValue)
      }}
      onClear={() => {
        setRfid('')
      }}
    />
  )

  return (
      <>
        <CustomBox isLaptop={isLaptop || isDesktop} showMobileHeader={showCollapsedTable}>
          {currentUser?.role === 'admin' ? (
            <TabsWrapper>
              <Tabs
                className="tabs-styling"
                activeKey={tab}
                onChange={(key) => {
                  const nextTab = key as typeof tab
                  setTab(nextTab)
                  // Keep prior list + filters in state: only fetch when this tab has never loaded
                  // (pagination unset). Search, pagination, dates (via search), and mutations still refetch.
                  if (nextTab === 'invitedUsers') {
                    if (invitationsPagination === undefined) {
                      fetchInvitations(1)
                    }
                  } else if (nextTab === 'users') {
                    if (usersPagination === undefined) {
                      fetchUsers(1)
                    }
                  }
                }}
              >
              <Tabs.TabPane key="users" tab={<span className="regular-cap">Users</span>}>
                {(isDesktop || isLaptop) && (
                  <Row
                    gutter={[16, 16]}
                    style={{
                      backgroundColor: '#fafafa',
                      padding: '16px 8px 12px',
                      marginBottom: 16,
                      alignItems: 'center',
                    }}
                  >
                    <Col flex="auto">
                      <Space wrap size={[16, 8]}>
                        {calendar}
                        <div style={{display: 'flex' }}>
                          <MultiTenantStyling>
                            <MultiTenantSelector
                              longerWidth={false}
                              isCombineRight
                              noChangeOnFocus={true}
                              onOptionsChange={(opts) => setUsersTabTenants(opts)}
                              onClear={() => setUsersTabTenants([])}
                            />
                          </MultiTenantStyling>

                          <UserSelectorStyling>
                            {renderMultiRfidSelector}
                          </UserSelectorStyling>
                          <UserSelectorStyling>
                            <SearchButtonContainer>
                            <NoIconSearch
                              svg={<></>}
                              placeholder={emailTxt}
                              value={usersSearchTerm}
                              onChange={handleEmailChange}
                              onPressEnter={runSearchForActiveList}
                            />
                              <SearchButtonWrapper>
                                <ButtonIcon
                                  icon={magnifyBlackSVG}
                                  loading={loading}
                                  disabled={loading}
                                  fetchData={runSearchForActiveList}
                                  withinSearchBar
                                />
                              </SearchButtonWrapper>
                            </SearchButtonContainer>
                          </UserSelectorStyling>
                        </div>
                      </Space>
                    </Col>
                  </Row>
                )}
                {usersTable('users')}
              </Tabs.TabPane>
              <Tabs.TabPane
                key="invitedUsers"
                tab={<span className="regular-cap">Invited Users</span>}
              >
                {(isDesktop || isLaptop) && (
                  <Row
                    gutter={[16, 16]}
                    style={{
                      backgroundColor: '#fafafa',
                      padding: '16px 8px 12px',
                      marginBottom: 16,
                      alignItems: 'center',
                    }}
                  >
                    <Col flex="auto">
                      <Space wrap size={[16, 8]}>
                        {calendar}
                        <div
                          style={{
                            display: 'flex',
                          }}
                        >
                          <MultiTenantStyling>
                            <MultiTenantSelector
                              longerWidth={true}
                              isCombineRight
                              noChangeOnFocus={true}
                              onOptionsChange={(opts) => setInvitedTabTenants(opts)}
                              onClear={() => setInvitedTabTenants([])}
                            />
                          </MultiTenantStyling>
                          <UserSelectorStyling>
                            <SearchButtonContainer>
                              <NoIconSearch
                                svg={<></>}
                                placeholder={emailTxt}
                                value={invitedSearchTerm}
                                onChange={handleEmailChange}
                                onPressEnter={runSearchForActiveList}
                              />
                              <SearchButtonWrapper>
                                <ButtonIcon
                                  icon={magnifyBlackSVG}
                                  loading={loading}
                                  disabled={loading}
                                  fetchData={runSearchForActiveList}
                                  withinSearchBar
                                />
                              </SearchButtonWrapper>
                            </SearchButtonContainer>
                          </UserSelectorStyling>
                        </div>
                        </Space>
                      </Col>
                      <Col>
                        <InviteButtonWrapper>
                          <ButtonModal
                            size="large"
                            click={handleAddUser}
                            icon={plusSVG}
                            text={inviteUser}
                            type="primary"
                          />
                        </InviteButtonWrapper>
                      </Col>
                    </Row>
                  )}
                {usersTable('invitations')}
                </Tabs.TabPane>

              <Tabs.TabPane
                key="plans"
                tab={<span className="regular-cap">{plansText}</span>}
              >
                {(isDesktop || isLaptop) && (
                  <TabsButtonWrapper className="plans-button">
                    <ButtonModal
                      size="large"
                      click={() => setShowCreatePlanForm(true)}
                      icon={plusSVG}
                      text={createPlanText}
                      type="primary"
                    />
                  </TabsButtonWrapper>
                )}
                <PlansRedesign
                  createPlanForm={createPlanForm}
                  onCancel={() => setShowCreatePlanForm(false)}
                />
              </Tabs.TabPane>
              <Tabs.TabPane
                key="subscriptions"
                tab={<span className="regular-cap">{subscriptionsText}</span>}
              >
                  {(isDesktop || isLaptop) && (
                    <Row>
                    <TabsButtonWrapper
                      className="subscriptions-header"
                      isLaptop={isLaptop || isDesktop}
                    >
                        <div style={{ display: 'flex' }}>
                          <UserSelectorNoTagRedesign
                            onUserSelected={handleSubscriptionEmailChange}
                            onSubmit={onSubmitSubscriptionSearch}
                            dropdownClassName={'not-in-modal-selctor'}
                          />
                        </div>
                        {isDesktop || isLaptop ? (
                          <ButtonModal
                            size="large"
                            click={() => setShowInviteForm(true)}
                            icon={plusSVG}
                            text={subscribeUserText}
                            type="primary"
                          />
                        ) : (
                          <InviteButtonCircleWrapper>
                            <ButtonIconCircle
                              icon={plusSVG}
                              disabled={false}
                              fetchData={() => setShowInviteForm(true)}
                              loading={false}
                              size="large"
                            />
                          </InviteButtonCircleWrapper>
                        )}
                      </TabsButtonWrapper>
                    </Row>
                  )}
                  <SubscriptionsRedesign
                    searchUser={subscriptionEmailSearch}
                    searchSubscriptions={searchSubscriptions}
                    onUserInvited={() => setShowInviteForm(false)}
                    showInviteForm={showInviteForm}
                  />
                </Tabs.TabPane>
              </Tabs>
            </TabsWrapper>
          ) : (
          usersTable('all')
          )}
        </CustomBox>

      {showInvitationForm && currentAugmentedUser && (
        <UserInvitationRedesign
          onCancel={(e: React.FormEvent<EventTarget>) => setShowInvitationForm(false)}
          onUserInvited={handleUserInvited}
        />
      )}

      <Modal
        visible={editUserFetchLoading || cloneFetchLoading || rfidFetchLoading}
        footer={null}
        closable
        destroyOnClose
        onCancel={() => {
          setEditUserFetchLoading(false)
          setCloneFetchLoading(false)
          setRfidFetchLoading(false)
          setEditUserNameForm(false)
          setShowCloneForm(false)
          setShowRFIDForm(false)
          setSelectedUser(undefined)
        }}
      >
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin size="large" />
        </div>
      </Modal>
      {selectedUser && showEditUserNameForm && !editUserFetchLoading && (
        <UserEditForm
          onCancel={() => setEditUserNameForm(false)}
          onUserEdit={onFinishUserEdit(() => setEditUserNameForm(false))}
          selectedUser={selectedUser}
        />
      )}
      {selectedUser && showCloneForm && !cloneFetchLoading && (
        <UserCloneForm
          onCancel={() => setShowCloneForm(false)}
          onFinish={onFinishUserEdit(() => setShowCloneForm(false))}
          selectedUser={selectedUser}
        />
      )}
      {showRFIDForm && selectedUser && !rfidFetchLoading && (
        <UserRFIDForm onCancel={closeRfidModal} selectedUser={selectedUser} />
      )}
    </>
  )
}
